import { createSlice } from '@reduxjs/toolkit';

// Map each feature eventKey to its category
const featureToCategoryMap = {
    'djpal': 'explore',
    'smart-recs': 'explore',
    'feed': 'explore',
    'whats-next': 'explore',
    'ai-set': 'explore',
    'visualize': 'library',
    'organize-tracks': 'library',
    'fix-metadata': 'library',
    'migrate-library': 'library',
    'fix-my-tracks': 'prepare',
    'match-loudness': 'prepare',
    'cue-points': 'prepare',
    'automix': 'prepare',
};

const NAV_STACK_MAX = 50;

// The mobile Library nav item covers a landing page plus three sub-pages.
// setActiveTab records the most recent of these so tapping Library after
// visiting another tab returns to where the user actually was (Spotify-style)
// instead of the generic landing.
const LIBRARY_SECTION_TABS = new Set(['library', 'likes', 'followed-artists', 'playlists']);

// Monotonic id for nav entries — see pushNavEntry. Session-scoped; only
// uniqueness matters, not the value.
let navIdSeq = 0;

const initialState = {
    files: [],
    progress: 0,
    isProcessing: false,
    errorMessage: "",
    activeTab: null,
    activeCategory: "explore",
    // Cmd+F "Find on this page" term — highlight/jump-to-match ONLY. Tabs
    // must never hide rows or scope an action on it (that's filterTerms).
    searchTerm: "",
    // Per-tab table-filter terms (TabFilterBox), keyed by tab id — currently
    // only the Library tab ('library') has a filter box; the tool tabs
    // deliberately have none (their scoping lives in the source picker +
    // status/type/LUFS filters). Per-tab so a term typed in one tab can never
    // invisibly hide rows in another.
    filterTerms: {},
    // Navbar "In Your Library" click → { filePath, nonce }. The Library tab
    // scrolls to the track and flash-highlights it; nonce lets the same track
    // re-trigger.
    libraryFocusTrack: null,
    // Last Library-section tab visited ('library' | 'likes' |
    // 'followed-artists' | 'playlists') — where the mobile Library nav
    // item returns to.
    lastLibraryTab: 'library',
    inputConcurrencyLevel: 3,
    cancelRequested: false,
    autoAnalyze: false,
    // App-level navigation history
    navStack: [],
    navIndex: -1,
    navSkipPush: false,
    // True while the OnboardingFlow overlay is showing. The native paywall
    // auto-present (App.js) waits for this to clear so the paywall isn't
    // dispatched behind the onboarding overlay (where it would be buried and
    // its once-per-session flag consumed without the user ever seeing it).
    onboardingActive: false,
    // False until OnboardingFlow's (async) decide() has determined whether it
    // will show. The paywall must wait for this — decide() awaits server prefs,
    // so a tier-resolve can fire the paywall BEFORE onboarding flips
    // onboardingActive=true, presenting it behind the overlay and burning the
    // once-per-session flag (the Skip-skips-paywall bug). Gate on decided too.
    onboardingDecided: false,
};

const appSlice = createSlice({
    name: 'app',
    initialState,
    reducers: {
        resetFilePath(state) {
            state.files = []
        },
        setFiles(state, action) {
            state.files = action.payload;
        },
        setProgress(state, action) {
            state.progress = action.payload;
        },
        setIsProcessing(state, action) {
            state.isProcessing = action.payload;
        },
        setErrorMessage(state, action) {
            state.errorMessage = action.payload;
        },
        setActiveTab(state, action) {
            state.activeTab = action.payload;
            if (LIBRARY_SECTION_TABS.has(action.payload)) {
                state.lastLibraryTab = action.payload;
            }
            // Auto-set category when a feature tab is activated (e.g. from sidebar)
            const category = featureToCategoryMap[action.payload];
            if (category) {
                state.activeCategory = category;
            }
        },
        setActiveCategory(state, action) {
            state.activeCategory = action.payload;
            state.activeTab = null; // Show the grid
        },
        setSearchTerm(state, action) {
            state.searchTerm = action.payload;
        },
        setFilterTerm(state, action) {
            const { tab, term } = action.payload;
            state.filterTerms[tab] = term;
        },
        setLibraryFocusTrack(state, action) {
            state.libraryFocusTrack = action.payload;
        },
        setInputConcurrencyLevel(state, action) {
            state.inputConcurrencyLevel = action.payload;
        },
        setCancelRequested(state, action) {
            state.cancelRequested = action.payload;
        },
        setAutoAnalyze(state, action) {
            state.autoAnalyze = action.payload;
        },
        pushNavEntry(state, action) {
            if (state.navSkipPush) {
                state.navSkipPush = false;
                return;
            }
            const trimmed = state.navStack.slice(0, state.navIndex + 1);
            // Session-unique entry id. Stack indexes get reused when a forward
            // branch is trimmed, so per-entry side state (DJPalTab's scroll
            // positions) keys on navId instead of navIndex.
            trimmed.push({ ...action.payload, navId: ++navIdSeq });
            while (trimmed.length > NAV_STACK_MAX) trimmed.shift();
            state.navStack = trimmed;
            state.navIndex = trimmed.length - 1;
        },
        replaceTopNavEntry(state, action) {
            if (state.navIndex < 0) return;
            // Replacing refreshes the entry's CONTENT — it's still the same
            // navigation stop, so the navId must survive (callers spread the
            // old entry, but don't rely on that).
            const prevId = state.navStack[state.navIndex]?.navId;
            state.navStack[state.navIndex] = { ...action.payload, navId: action.payload.navId ?? prevId };
        },
        setNavIndex(state, action) {
            state.navIndex = action.payload;
        },
        setNavSkipPush(state, action) {
            state.navSkipPush = action.payload;
        },
        restoreNavState(state, action) {
            const { category, tab } = action.payload;
            state.activeCategory = category;
            state.activeTab = tab;
        },
        // Logout / account switch: put navigation back where a fresh launch
        // starts — the hub grid on desktop, and App.js's "web defaults to
        // Feed" effect takes null → 'feed' on web and native. The nav stack
        // empties too, so Back can never carry the next account into the
        // pages the previous session left open. Onboarding flags and the
        // file/progress state stay: they are device and session facts, not
        // the account's position in the app.
        resetNavigation(state) {
            state.activeTab = initialState.activeTab;
            state.activeCategory = initialState.activeCategory;
            state.searchTerm = initialState.searchTerm;
            state.filterTerms = {};
            state.libraryFocusTrack = null;
            state.lastLibraryTab = initialState.lastLibraryTab;
            state.navStack = [];
            state.navIndex = -1;
            state.navSkipPush = false;
        },
        setOnboardingActive(state, action) {
            state.onboardingActive = action.payload;
        },
        setOnboardingDecided(state, action) {
            state.onboardingDecided = action.payload;
        },
    },
});

export const {
    setFiles,
    setProgress,
    setIsProcessing,
    setErrorMessage,
    setActiveTab,
    setActiveCategory,
    setSearchTerm,
    setFilterTerm,
    setLibraryFocusTrack,
    setInputConcurrencyLevel,
    resetFilePath,
    setCancelRequested,
    setAutoAnalyze,
    pushNavEntry,
    replaceTopNavEntry,
    setNavIndex,
    setNavSkipPush,
    restoreNavState,
    resetNavigation,
    setOnboardingActive,
    setOnboardingDecided,
} = appSlice.actions;

export { featureToCategoryMap };

export default appSlice.reducer;
